import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Pencil, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Button } from '@/components/ui/Button';
import { Table } from '@/components/ui/Table/Table';
import { Badge } from '@/components/ui/Badge/Badge';
import { useNotificationStore } from '@/stores/notificationStore';
import { blogService } from '@/services/blogService';
import type { BlogPost } from '@/types/blog';

const statusVariant: Record<string, 'success' | 'secondary' | 'outline'> = {
  published: 'success',
  draft: 'secondary',
  archived: 'outline',
};

export function BlogPostsList() {
  const navigate = useNavigate();
  const notify = useNotificationStore();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(20);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const result = await blogService.listPosts({
        status: filters.status || undefined,
        search: filters.search || undefined,
        page: currentPage,
        pageSize: currentPageSize,
      });
      setPosts(result.data);
      setTotal(result.total);
    } catch (err: any) {
      notify.error(err.message || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, [currentPage, currentPageSize, filters]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const handleFilter = (newFilters: Record<string, string>) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePaginate = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setCurrentPageSize(pageSize);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this post? This cannot be undone.')) return;
    try {
      await blogService.deletePost(id);
      notify.success('Post deleted');
      loadPosts();
    } catch (err: any) {
      notify.error(err.message || 'Failed to delete post');
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns = [
    {
      key: 'title',
      title: 'Title',
      sortable: true,
      render: (_: any, post: BlogPost) => (
        <div className="flex items-center gap-3">
          {post.featured_image && (
            <img
              src={post.featured_image}
              alt=""
              className="w-10 h-10 rounded object-cover flex-shrink-0"
            />
          )}
          <div className="min-w-0">
            <div className="font-medium text-foreground truncate">{post.title}</div>
            <div className="text-xs text-muted-foreground truncate">/blog/{post.slug}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      width: '100px',
      render: (value: string) => (
        <Badge variant={statusVariant[value] || 'outline'}>
          {value.charAt(0).toUpperCase() + value.slice(1)}
        </Badge>
      ),
    },
    {
      key: 'category_name',
      title: 'Category',
      width: '130px',
      render: (value: string | null) =>
        value ? <Badge variant="outline">{value}</Badge> : <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'author_name',
      title: 'Author',
      width: '130px',
      render: (value: string | null) =>
        value || <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'published_at',
      title: 'Published',
      width: '130px',
      sortable: true,
      render: (value: string | null) => (
        <span className={value ? 'text-foreground' : 'text-muted-foreground'}>
          {formatDate(value)}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: '120px',
      align: 'right' as const,
      render: (_: any, post: BlogPost) => (
        <div className="flex items-center justify-end gap-1">
          {post.status === 'published' && (
            <button
              onClick={(e) => { e.stopPropagation(); window.open(`/blog/${post.slug}`, '_blank'); }}
              className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
              title="View live"
            >
              <Eye className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/admin/blog/${post.id}`); }}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
            title="Edit"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(post.id); }}
            className="p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  const tableFilters = [
    {
      key: 'status',
      label: 'Status',
      options: [
        { label: 'Draft', value: 'draft' },
        { label: 'Published', value: 'published' },
        { label: 'Archived', value: 'archived' },
      ],
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Blog Posts"
        description="Manage blog posts and content."
      >
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/blog/categories')}>
            Categories
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/media')}>
            Media Library
          </Button>
          <Button variant="primary" size="sm" onClick={() => navigate('/admin/blog/new')}>
            New Post
          </Button>
        </div>
      </PageHeader>

      <Table
        data={posts}
        columns={columns}
        loading={loading}
        searchable
        searchPlaceholder="Search posts..."
        filters={tableFilters}
        pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: [10, 20, 50] }}
        emptyText="No blog posts yet."
        serverSide
        onFilter={handleFilter}
        onPaginate={handlePaginate}
        totalItems={total}
      />
    </PageContainer>
  );
}
